import {
  AnchorButtonProps as BlueprintAnchorButtonProps,
  InputGroup as BlueprintInputGroup,
  InputGroupProps2 as BlueprintInputGroupProps2,
  PopoverPosition as BlueprintPopoverPosition,
} from '@blueprintjs/core';
import { Close } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { InputButton } from 'bones/design-system/components/form/InputButton';
import {
  Icon,
  IconComponentType,
} from 'bones/design-system/components/icons/Icon';
import {
  SystemInfo,
  SystemInfoProps,
} from 'bones/design-system/components/notifications/SystemInfo';
import {
  SystemMessage,
  SystemMessageProps,
} from 'bones/design-system/components/notifications/SystemMessage';
import {
  Body,
  BodyProps,
} from 'bones/design-system/components/typography/Body';

const LABEL_SIZE_BY_INPUT_SIZE: Record<InputProps['size'], BodyProps['size']> =
  {
    'lg-inside': 'sm',
    'lg-outside': 'md',
    md: 'md',
    sm: 'sm',
  };

export type InputProps = Omit<
  BlueprintInputGroupProps2,
  | 'defaultValue'
  | 'fill'
  | 'inputClassName'
  | 'intent'
  | 'large'
  | 'leftElement'
  | 'leftIcon'
  | 'round'
  | 'small'
  | 'size'
> & {
  'data-testid'?: never;
  clearable?: boolean;
  clearTestId?: string;
  divider?: boolean;
  helpProps?: Omit<SystemMessageProps, 'variant'>;
  icon?: IconComponentType;
  infoProps?: Omit<SystemInfoProps, 'position'>;
  invalid?: boolean;
  label: string;
  minimal?: boolean;
  onClear?: BlueprintAnchorButtonProps['onClick'];
  onSelectorClick?: BlueprintAnchorButtonProps['onClick'];
  rightElement?: React.ReactNode;
  selectorIcon?: IconComponentType;
  selectorTestId?: string;
  size: 'lg-inside' | 'lg-outside' | 'md' | 'sm';
  testId?: string;
};

export const Input: React.VFC<InputProps> = ({
  className,
  clearable = true,
  clearTestId,
  disabled,
  divider = true,
  helpProps,
  icon,
  id,
  infoProps,
  inputRef,
  invalid,
  label,
  minimal,
  onClear,
  onSelectorClick,
  placeholder,
  required,
  rightElement,
  selectorIcon,
  selectorTestId,
  size,
  testId,
  value = '',
  ...props
}) => (
  <div
    className={classNames(className, 'vb-input', `vb-input-size--${size}`, {
      'vb-input--icon': icon,
      'vb-input--minimal': minimal && !label,
      'vb-input-state--empty': !value && !placeholder,
      'vb-input-state--disabled': disabled,
      'vb-input-state--invalid': invalid,
    })}
  >
    {label ? (
      <label className="vb-input-label" htmlFor={id}>
        <Body size={LABEL_SIZE_BY_INPUT_SIZE[size]}>{label}</Body>
        {required ? (
          <Body
            className="vb-input-label-required"
            size={LABEL_SIZE_BY_INPUT_SIZE[size]}
          >
            *
          </Body>
        ) : null}
        {infoProps ? (
          <SystemInfo
            position={BlueprintPopoverPosition.TOP}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...infoProps}
          />
        ) : null}
      </label>
    ) : null}
    <BlueprintInputGroup
      className="vb-input-group"
      data-testid={testId}
      disabled={disabled}
      id={id}
      inputClassName="vb-input-input"
      inputRef={inputRef}
      leftElement={
        !minimal && icon ? (
          <Icon className="vb-input-icon" IconComponent={icon} size="sm" />
        ) : undefined
      }
      placeholder={placeholder}
      rightElement={
        <>
          {clearable && value ? (
            <>
              {!divider || minimal || selectorIcon ? null : (
                <div className="vb-input-divider" />
              )}
              <InputButton
                disabled={disabled}
                icon={Close}
                onClick={onClear}
                testId={clearTestId}
              />
            </>
          ) : null}
          {selectorIcon ? (
            <>
              {divider ? <div className="vb-input-divider" /> : null}
              <InputButton
                disabled={disabled}
                icon={selectorIcon}
                onClick={onSelectorClick}
                testId={selectorTestId}
              />
            </>
          ) : null}
          {minimal && icon ? (
            <Icon className="vb-input-icon" IconComponent={icon} size="sm" />
          ) : null}
          {rightElement}
        </>
      }
      value={value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
    {helpProps ? (
      <SystemMessage
        variant={invalid ? 'alert' : 'text'}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...helpProps}
      />
    ) : null}
  </div>
);
